import { Avatar, Button, Chip, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import classNames from 'classnames'
import NumberFormat from 'react-number-format'
import { useHistory } from 'react-router-dom'
import { useRiskProfileColor } from '../../hooks/useRiskProfileColor'
import { useExchangeRate } from '../../hooks/useExchangeRate'
import { Client, ClientPortfolioType } from '../../redux/reducers/clients'
import { Pages } from '../../router/router'
import IconButton from '@mui/material/IconButton'
import { openDialog } from '../../redux/actions/dialogActions'
import { CLIENT_INFORMATION_DIALOG_ID, DEPOSIT_DIALOG_ID } from '../../constants/dialogConstants'
import Edit from '@mui/icons-material/EditRounded'
import Add from '@mui/icons-material/AddRounded';
import { useDispatch } from 'react-redux'
import CardWrapper from '../common/CardWrapper'
import { formatNumber } from '../../utils/numbersFormatter'
import addQuarters from 'date-fns/addQuarters'
import { format, addDays, addMonths, isBefore } from 'date-fns'
import { newThemeObject } from '../../newTheme'
import { tokens } from '../../styles/tokens'
import { TransparentButton } from '../common/TransparentButton'
import { useTranslation } from 'react-i18next'
import { useSelector } from '../../redux/hooks/useSelector'
import { APEX, IMPERIUM_FINANCE } from '../../constants/businessTerritory'
import { computeClientLtv, computeClientLtvTopUpCzk } from '../../utils/ltv'
import { isApexUser } from '../../utils/apex'
import { isDistributorUser } from '../../utils/distributor'
import { Portfolio } from '../../redux/reducers/portfolio'

const useStyles = makeStyles(theme => ({
  top: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    width: '100%',
  },
  name: {
    fontWeight: 600,
    color: tokens.color.neutral[900],
    margin: 0,
    fontSize: '0.8125rem',
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  subtext: {
    fontSize: '0.6875rem',
    margin: 0,
    fontWeight: 400,
    color: tokens.color.neutral[500],
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  phoneNumber: {
    fontSize: '0.6rem',
    color: newThemeObject.palette.main.lightGray,
    margin: 0,
  },
  button: {
    borderRadius: tokens.radius.md,
    padding: 0,
    cursor: 'pointer',
    transition: `box-shadow ${tokens.transition.base}`,
    '&:hover': {
      boxShadow: tokens.shadow.md,
    },
    '&:hover $actions': {
      opacity: 1,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    opacity: 0.35,
    transition: `opacity ${tokens.transition.fast}`,
  },
  info: {
    minWidth: 0,
    flexGrow: 1,
  },
  metricsRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    width: '100%',
  },
  metric: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: '1 1 100px',
  },
  metricLabel: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: tokens.color.neutral[500],
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  metricValue: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: tokens.color.neutral[900],
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontVariantNumeric: 'tabular-nums',
  },
  metricValueLarge: {
    fontSize: '1.21875rem',
    fontWeight: 700,
  },
  balancePositive: {
    color: tokens.color.success[700],
  },
  balanceNegative: {
    color: tokens.color.warning[700],
  },
  validitySuccess: {
    color: tokens.color.success[700],
  },
  validityWarning: {
    color: tokens.color.warning[700],
  },
  validityError: {
    color: tokens.color.error[700],
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  iconButton: {
    width: '1.5rem',
    height: '1.5rem',
  },
  avatar: {
    width: '2.25rem',
    height: '2.25rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: tokens.color.primary[100],
    color: tokens.color.primary[700],
    flexShrink: 0,
  },
  whole: {
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    borderRadius: tokens.radius.md,
    border: `1px solid ${tokens.color.neutral[200]}`,
    boxShadow: 'none',
  },
  client: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
  },
}))

interface Props {
  client: Client
}

export const ClientTile: React.FC<Props> = ({ client }) => {
  const classes = useStyles()
  const history = useHistory()
  const riskProfileColor = useRiskProfileColor(client)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const user = useSelector(state => state.auth.user)
  const rates = useSelector(state => state.currency.rates)
  const exchangeRate = useExchangeRate()

  const onWholeClick = () => {
    history.push(Pages.ClientDetail + `/${client.clientId}`)
  }

  const onAddClick = () => {
    dispatch(
      openDialog(DEPOSIT_DIALOG_ID, {
        client,
      })
    )
  }

  const onEditClick = () => {
    dispatch(
      openDialog(CLIENT_INFORMATION_DIALOG_ID, {
        dialogTitle: t('CLIENT_EDIT'),
        client,
        type: 'edit',
      })
    )
  }

  const sumAmounts = (portfolios: ClientPortfolioType[]) => {
    let sum = 0;
    portfolios.forEach(portfolio => {
      sum += portfolio.amount ? portfolio.amount : 0;
    });
    return sum;
  }

  const sumAccountsBalanceCzk = (portfolios: ClientPortfolioType[]) => {
    let sum = 0;
    portfolios.forEach(portfolio => {
      sum += exchangeRate(portfolio.currency, 'CZK', portfolio.accountsBalance || 0, rates)
    });
    return sum;
  }

  const hasPortfolios = client && client.portfolios && client.portfolios.length > 0
  const portfolioMarketValue = formatNumber(
    Math.round(((hasPortfolios ? sumAmounts(client.portfolios) : 0.0) + Number.EPSILON) * 100) / 100
  )
  const portfolioCurrency = hasPortfolios ? client.portfolios[0].currency : ''
  const accountsBalanceCzkValue =
    Math.round(((hasPortfolios ? sumAccountsBalanceCzk(client.portfolios) : 0.0) + Number.EPSILON) * 100) / 100
  const accountsBalanceCzk = formatNumber(accountsBalanceCzkValue, { suffix: 'CZK', space: true })
  const isImperiumUser = !!(user && user.businessTerritoryId === IMPERIUM_FINANCE)
  const ltvValue = hasPortfolios ? computeClientLtv(client.portfolios, exchangeRate, rates) : 0
  const ltvText = `${formatNumber(ltvValue * 100)} %`
  const ltvTopUp50 = formatNumber(hasPortfolios ? computeClientLtvTopUpCzk(client.portfolios, exchangeRate, rates, 0.5) : 0, { suffix: 'CZK', space: true })
  const ltvTopUp70 = formatNumber(hasPortfolios ? computeClientLtvTopUpCzk(client.portfolios, exchangeRate, rates, 0.7) : 0, { suffix: 'CZK', space: true })
  const questionnaireValidToDate = client.investmentQuestionnaireValidTo
    ? new Date(client.investmentQuestionnaireValidTo)
    : null
  const questionnaireValidTo = questionnaireValidToDate
    ? format(questionnaireValidToDate, 'dd/MM/yyyy')
    : t('QUESTIONNAIRE_NOT_FILLED')

  const getQuestionnaireValidityClass = () => {
    if (!questionnaireValidToDate || isNaN(questionnaireValidToDate.getTime())) return classes.validityError
    const today = new Date()
    if (isBefore(questionnaireValidToDate, today)) return classes.validityError
    if (isBefore(questionnaireValidToDate, addDays(today, 14))) return classes.validityError
    if (isBefore(questionnaireValidToDate, addMonths(today, 3))) return classes.validityWarning
    return classes.validitySuccess
  }

  let avatarPlaceholder = ''

  if (client.firstName) {
    avatarPlaceholder += client.firstName.charAt(0)
  }

  if (client.surname) {
    avatarPlaceholder += client.surname.replace(' ', '').charAt(0)
  }
  return (
    <div className={classes.button} onClick={onWholeClick}>
      <CardWrapper className={classes.whole}>
        <span className={classes.top}>
          <Avatar className={classes.avatar}>{avatarPlaceholder}</Avatar>
          <span className={classes.info}>
            {client.company ? (
              <>
                <p className={classes.name}>{client.companyName}</p>
                <p className={classes.subtext}>{client.firstName} {client.surname}</p>
              </>
            ) : (
              <p className={classes.name}>{client.firstName} {client.surname}</p>
            )}
            {client.incomplete && (
              <Chip size="small" color="warning" variant="outlined" label={t('CLIENT_INCOMPLETE')} />
            )}
          </span>
          <span className={classes.right}>
            <span className={classes.actions}>
              {isApexUser(user) && !isDistributorUser(user) && (
                <IconButton
                  onClick={e => {
                    e.stopPropagation()
                    onAddClick()
                  }}
                  className={classes.iconButton}
                >
                  <Add fontSize="small" />
                </IconButton>
              )}
              {!isDistributorUser(user) && (
                <IconButton
                  onClick={e => {
                    e.stopPropagation()
                    onEditClick()
                  }}
                  className={classes.iconButton}
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
            </span>
          </span>
        </span>
        <span className={classes.metricsRow}>
          <span className={classes.metric}>
            <p className={classes.metricLabel} title={t('PORTFOLIO_MARKET_VALUE')}>{t('PORTFOLIO_MARKET_VALUE')}</p>
            <p
              className={classNames(classes.metricValue, classes.metricValueLarge)}
              title={`${portfolioMarketValue} ${portfolioCurrency}`}
            >
              {portfolioMarketValue} {portfolioCurrency}
            </p>
          </span>
        </span>
        <span className={classes.metricsRow}>
          {isImperiumUser ? (
            <span className={classes.metric}>
              <p className={classes.metricLabel} title={t('LTV')}>{t('LTV')}</p>
              <p className={classes.metricValue} title={`${t('ACCOUNTS_BALANCE_CZK')}: ${accountsBalanceCzk}`}>
                {ltvText}
              </p>
            </span>
          ) : (
            <span className={classes.metric}>
              <p className={classes.metricLabel} title={t('ACCOUNTS_BALANCE_CZK')}>{t('ACCOUNTS_BALANCE_CZK')}</p>
              <p
                className={classNames(classes.metricValue, accountsBalanceCzkValue >= 0 ? classes.balancePositive : classes.balanceNegative)}
                title={accountsBalanceCzk}
              >
                {accountsBalanceCzk}
              </p>
            </span>
          )}
          <span className={classes.metric}>
            <p className={classes.metricLabel} title={t('QUESTIONNAIRE_VALIDITY_DATE')}>{t('QUESTIONNAIRE_VALIDITY_DATE')}</p>
            <p className={classNames(classes.metricValue, getQuestionnaireValidityClass())} title={questionnaireValidTo}>{questionnaireValidTo}</p>
          </span>
        </span>
        {isImperiumUser && (
          <span className={classes.metricsRow}>
            <span className={classes.metric}>
              <p className={classes.metricLabel} title={t('LTV_TOPUP_50')}>{t('LTV_TOPUP_50_SHORT')}</p>
              <p className={classes.metricValue} title={ltvTopUp50}>{ltvTopUp50}</p>
            </span>
            <span className={classes.metric}>
              <p className={classes.metricLabel} title={t('LTV_TOPUP_70')}>{t('LTV_TOPUP_70_SHORT')}</p>
              <p className={classes.metricValue} title={ltvTopUp70}>{ltvTopUp70}</p>
            </span>
          </span>
        )}
      </CardWrapper>
    </div>
  )
}
